import {createApp, h, provide} from "vue"
import { createI18n } from 'vue-i18n'
import App from "./App.vue"
import "./main.css"
import {ApolloClients, provideApolloClient} from "@vue/apollo-composable"
import {createApolloClient} from "./graphql/apollo-client"
import router from "./router"
import Notifications from "notiwind"
import {loadNavigatorLocale} from "./date"
import {createLogger} from "./logger"
import {provideAppContext} from "./AppContext"
import {useActivityIndicator} from "./community/composables/useActivityIndicator"

import {Uploads, UploadsKey} from "./upload/Uploads"
import {useHead} from "@unhead/vue"
import {createHead} from "@unhead/vue/client"
import * as Sentry from "@sentry/vue";
import auth, { refreshTokenIfNeeded, token } from "./auth/keycloak";
import "@jamescoyle/svg-icon";
import VueApexCharts from "vue3-apexcharts"
import deDE from "./locales/de-DE.json"
import type { MessageSchema } from "./locales"

const logger = createLogger('main')

declare module 'vue' {
  interface ComponentCustomProperties {
    $auth: typeof auth
  }
}

// If auth bootstrap fails (Keycloak unreachable, init rejects) or resolves
// unauthenticated, the Vue app never mounts and #app stays empty — a blank white
// page. Render a minimal static error/retry state instead (resilience-03).
function renderStartupError() {
  const el = document.getElementById("app")
  if (el) {
    el.innerHTML =
      '<div style="display:flex;min-height:100vh;flex-direction:column;align-items:center;justify-content:center;gap:1rem;font-family:sans-serif;text-align:center;padding:1rem;">' +
      '<p>Anmeldung nicht möglich. Bitte versuche es erneut.</p>' +
      '<button onclick="location.reload()" style="padding:0.5rem 1rem;border-radius:0.375rem;border:0;background:#2563eb;color:#fff;cursor:pointer;">Erneut versuchen</button>' +
      '</div>'
  }
}

auth.init().then(authenticated => {
    logger.debug("Keycloak auth init: %s", authenticated)

    if (authenticated) {
      const app = createApp({
        setup() {
          const apolloClient = createApolloClient(token, refreshTokenIfNeeded)
          provide(ApolloClients, {default: apolloClient})
          provideApolloClient(apolloClient)

          const uploads = new Uploads(token, refreshTokenIfNeeded, apolloClient)
          provide(UploadsKey, uploads)

          useHead({
            titleTemplate: (title) => title ? `${title} - NintendoFans.de Redaktion` : 'NintendoFans.de Redaktion',
          })

          const { state: communityIndicator } = useActivityIndicator()
          provideAppContext({ communityIndicator })
        },
        render: () => h(App as never),
      })

      app.config.compilerOptions.isCustomElement = (tag) => tag == 'svg-icon'
      app.config.globalProperties.$auth = auth

      Sentry.init({
        app,
        // Kein DSN ausserhalb von PROD = kein Versand. Das Taggen mit `environment`
        // sollte dev-Telemetrie filterbar machen (observability-03), tatsaechlich
        // stammten am 23.09. 25 der 54 offenen CMS-Issues aus lokalen Laeufen — jede
        // INTERNAL_ERROR-UUID bildet ein eigenes Issue, was die Triage-Liste flutet.
        // Gleiche Gatterung wie im Frontend (`entry-client.ts` ruft bootSentry nur
        // in PROD).
        dsn: import.meta.env.PROD
          ? "https://29afbf0d6a724fe981feb649f907eff9@glitchtip.nintendofans.rocks/2"
          : undefined,
        // Release korreliert Prod-Ereignisse mit einem deployten Build (observability-03).
        environment: import.meta.env.MODE,
        release: APP_RELEASE,
        // Only propagate trace headers to localhost in dev builds.
        tracePropagationTargets: import.meta.env.PROD
          ? [/^https:\/\/nfb\.lins\.io\//]
          : ["localhost", /^https:\/\/nfb\.lins\.io\//],
        integrations: [
          Sentry.browserTracingIntegration({router}),
          // Explicit privacy config for a privileged admin tool — do NOT rely on
          // library defaults. Masks all text/inputs and blocks media so editorial
          // content and PII (usernames, emails in comments/user views) are never
          // reconstructed off-site in GlitchTip.
          Sentry.replayIntegration({
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
          }),
          Sentry.vueIntegration({
            tracingOptions: {
              trackComponents: true,
              timeout: 1000,
              hooks: ["mount", "update", "unmount"],
            },
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: 0.01,
        // Session Replay
        replaysSessionSampleRate: 0.01,
        // Every errored admin session is captured; PII is masked by the replay
        // privacy config above, so full-rate error capture is acceptable.
        replaysOnErrorSampleRate: 1.0,
      });

      // Attribute error events to the editor (Keycloak subject id only — no name/email)
      // so operators can tell whether a bug affects one account or everyone (observability-07).
      Sentry.setUser({ id: auth.keycloak.subject })

      const i18n = createI18n<[MessageSchema], 'de-DE'>({
        legacy: false,
        locale: 'de-DE',
        messages: {
          'de-DE': deDE
        }
      })

      app.use(i18n)
      app.use(router)
      app.use(createHead())
      app.use(Notifications)
      app.use(VueApexCharts)


      // Zuerst sicherstellen, dass das Browser locale geladen wurde
      loadNavigatorLocale()
        .then(
          (locale) => {
            logger.debug(`Loaded navigator locale ${locale}`)
          },
          (reason) => {
            logger.warn(`Failed to load a proper locale: ${reason}`)
          },
        )
        .finally(() => {
          // anschließend die App "starten"
          app.mount("#app")
        })
    } else {
      logger.error('Keycloak init not authenticated')
      renderStartupError()
    }
  })
  .catch((error) => {
    logger.error({ error }, 'Keycloak init failed')
    renderStartupError()
  })
